/**
 * SSR-safe wrapper around VueUse's useBreakpoints.
 *
 * Problem: @vueuse/core v10 has no ssrWidth support (added in v11+). When
 * breakpoints are evaluated during SSR, window is undefined so VueUse returns
 * false for all "greater" checks. The client re-evaluates with the real window
 * and may get different values, causing Vue hydration mismatches.
 *
 * Fix: return false for all "greater/equal" checks and true for all
 * "smaller/equal" checks until the component is mounted (matching SSR's
 * mobile-first defaults). After mount, return the real values. Vue hydration
 * sees identical output on server and client initial render, then the client
 * re-renders with the real values post-mount — no mismatch.
 */
import { useBreakpoints as _useBreakpoints } from "@vueuse/core";

export function useBreakpoints<K extends string>(
	breakpoints: Record<K, number | string>
) {
	const isMounted = ref(false);

	onMounted(() => {
		isMounted.value = true;
	});

	const bp = _useBreakpoints(breakpoints);

	return {
		/** true when screen width >= breakpoint key. Always false before mount. */
		isGreaterOrEqual: (k: K): boolean =>
			isMounted.value && bp.isGreaterOrEqual(k),

		/** true when screen width > breakpoint key. Always false before mount. */
		isGreater: (k: K): boolean => isMounted.value && bp.isGreater(k),

		/** true when screen width < breakpoint key. Always true before mount (mobile-first). */
		isSmaller: (k: K): boolean => !isMounted.value || bp.isSmaller(k),

		/** true when screen width <= breakpoint key. Always true before mount (mobile-first). */
		isSmallerOrEqual: (k: K): boolean =>
			!isMounted.value || bp.isSmallerOrEqual(k),

		/** ComputedRef version of isGreaterOrEqual, for use in computed() calls. */
		greaterOrEqual: (k: K) =>
			computed(() => isMounted.value && bp.greaterOrEqual(k).value),

		/** ComputedRef version of isSmallerOrEqual, for use in computed() calls. */
		smallerOrEqual: (k: K) =>
			computed(() => !isMounted.value || bp.smallerOrEqual(k).value),

		/** Current active breakpoints array. Delegated to VueUse as-is. */
		current: () => bp.current(),
	};
}
